import { supabase } from './supabase';

export type StripeConnectScope = 'team' | 'organization';

export interface StripeConnectAccountSnapshot {
  stripe_account_id: string | null;
  stripe_account_status: string;
  charges_enabled: boolean;
  details_submitted: boolean;
  payouts_enabled: boolean;
}

const DISMISS_PERMANENT_PREFIX = 'podium_stripe_onboarding_dismissed_';
const DISMISS_LATER_PREFIX = 'podium_stripe_onboarding_later_';
const REMIND_LATER_MS = 24 * 60 * 60 * 1000;

function storageKey(scope: StripeConnectScope, entityId: string, permanent: boolean) {
  const prefix = permanent ? DISMISS_PERMANENT_PREFIX : DISMISS_LATER_PREFIX;
  return `${prefix}${scope}_${entityId}`;
}

export function isStripeOnboardingDismissed(scope: StripeConnectScope, entityId: string): boolean {
  try {
    if (localStorage.getItem(storageKey(scope, entityId, true)) === '1') return true;
    const later = localStorage.getItem(storageKey(scope, entityId, false));
    if (!later) return false;
    const until = Number(later);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

export function dismissStripeOnboarding(
  scope: StripeConnectScope,
  entityId: string,
  mode: 'permanent' | 'later',
) {
  try {
    if (mode === 'permanent') {
      localStorage.setItem(storageKey(scope, entityId, true), '1');
      localStorage.removeItem(storageKey(scope, entityId, false));
      return;
    }
    localStorage.setItem(
      storageKey(scope, entityId, false),
      String(Date.now() + REMIND_LATER_MS),
    );
  } catch {
    // ignore
  }
}

export function clearStripeOnboardingDismissal(scope: StripeConnectScope, entityId: string) {
  try {
    localStorage.removeItem(storageKey(scope, entityId, true));
    localStorage.removeItem(storageKey(scope, entityId, false));
  } catch {
    // ignore
  }
}

export async function fetchTeamStripeAccount(
  teamId: string,
): Promise<StripeConnectAccountSnapshot | null> {
  const { data } = await supabase
    .from('team_stripe_accounts')
    .select(
      'stripe_account_id, stripe_account_status, charges_enabled, details_submitted, payouts_enabled',
    )
    .eq('team_id', teamId)
    .maybeSingle();
  return data;
}

export async function fetchOrganizationStripeAccount(
  organizationId: string,
): Promise<StripeConnectAccountSnapshot | null> {
  const { data } = await supabase
    .from('organization_stripe_accounts')
    .select(
      'stripe_account_id, stripe_account_status, charges_enabled, details_submitted, payouts_enabled',
    )
    .eq('organization_id', organizationId)
    .maybeSingle();
  return data;
}

export function isStripeReady(account: StripeConnectAccountSnapshot | null | undefined): boolean {
  return !!account?.charges_enabled && account.stripe_account_status === 'enabled';
}

async function getAccessToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Please sign in to connect Stripe.');
  return session.access_token;
}

function stripeConnectCallbackUrl(): string {
  return `${window.location.origin}/stripe-connect-callback`;
}

export async function startTeamStripeConnect(
  teamId: string,
  returnUrl?: string,
  refreshUrl?: string,
): Promise<string> {
  const token = await getAccessToken();
  const appUrl = window.location.origin;
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-stripe-connect-account`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        teamId,
        returnUrl: returnUrl || `${appUrl}/dashboard?stripe_connect=complete&scope=team&entityId=${teamId}`,
        refreshUrl: refreshUrl || `${appUrl}/dashboard?stripe_connect=refresh&scope=team&entityId=${teamId}`,
      }),
    },
  );
  const result = await response.json();
  if (!response.ok || !result.success || !result.onboardingUrl) {
    throw new Error(result.error || 'Failed to start Stripe onboarding');
  }
  return result.onboardingUrl as string;
}

export async function startTeamStripeConnectOAuth(teamId: string): Promise<string> {
  const token = await getAccessToken();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/start-stripe-connect-oauth`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        scope: 'team',
        entityId: teamId,
        redirectUri: stripeConnectCallbackUrl(),
      }),
    },
  );
  const result = await response.json();
  if (!response.ok || !result.success || !result.authorizeUrl) {
    throw new Error(result.error || 'Failed to start Stripe sign-in');
  }
  return result.authorizeUrl as string;
}

export async function startOrganizationStripeConnectOAuth(organizationId: string): Promise<string> {
  const token = await getAccessToken();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/start-stripe-connect-oauth`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        scope: 'organization',
        entityId: organizationId,
        redirectUri: stripeConnectCallbackUrl(),
      }),
    },
  );
  const result = await response.json();
  if (!response.ok || !result.success || !result.authorizeUrl) {
    throw new Error(result.error || 'Failed to start Stripe sign-in');
  }
  return result.authorizeUrl as string;
}

export async function completeStripeConnectOAuth(code: string, state: string) {
  const token = await getAccessToken();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/complete-stripe-connect-oauth`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ code, state }),
    },
  );
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Could not complete Stripe sign-in');
  }
  return result as {
    scope: StripeConnectScope;
    entityId: string;
    stripeAccountId: string;
    status: string;
    chargesEnabled: boolean;
  };
}

export async function startOrganizationStripeConnect(
  organizationId: string,
  returnUrl?: string,
  refreshUrl?: string,
): Promise<string> {
  const token = await getAccessToken();
  const appUrl = window.location.origin;
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-org-stripe-connect-account`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        organizationId,
        returnUrl:
          returnUrl ||
          `${appUrl}/dashboard?stripe_connect=complete&scope=organization&entityId=${organizationId}`,
        refreshUrl:
          refreshUrl ||
          `${appUrl}/dashboard?stripe_connect=refresh&scope=organization&entityId=${organizationId}`,
      }),
    },
  );
  const result = await response.json();
  if (!response.ok || !result.success || !result.onboardingUrl) {
    throw new Error(result.error || 'Failed to start Stripe onboarding');
  }
  return result.onboardingUrl as string;
}

export async function startConnectPlatformSubscription(
  scope: StripeConnectScope,
  entityId: string,
): Promise<{ subscriptionId: string; status: string; alreadyActive?: boolean }> {
  const token = await getAccessToken();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/start-connect-platform-subscription`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ scope, entityId }),
    },
  );
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Failed to start platform subscription billing');
  }
  return {
    subscriptionId: result.subscriptionId as string,
    status: result.status as string,
    alreadyActive: result.alreadyActive as boolean | undefined,
  };
}

export async function syncStripeConnect(scope: StripeConnectScope, entityId: string) {
  const token = await getAccessToken();
  const endpoint =
    scope === 'team'
      ? 'sync-stripe-connect-status'
      : 'sync-org-stripe-connect-status';
  const body =
    scope === 'team' ? { teamId: entityId } : { organizationId: entityId };

  await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function linkStripeConnectAccount(
  sourceScope: StripeConnectScope,
  sourceId: string,
  targetScope: StripeConnectScope,
  targetId: string,
): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/link-stripe-connect-account`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        sourceScope,
        sourceId,
        targetScope,
        targetId,
      }),
    },
  );
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Could not link Stripe account');
  }
}

export function hasLinkableStripeAccount(
  account: StripeConnectAccountSnapshot | null | undefined,
): boolean {
  return !!account?.stripe_account_id;
}

export interface ManagedStripeEntity {
  scope: StripeConnectScope;
  id: string;
  name: string;
}

export async function findLinkableStripeSource(
  entities: ManagedStripeEntity[],
): Promise<(ManagedStripeEntity & { account: StripeConnectAccountSnapshot }) | null> {
  for (const entity of entities) {
    const account =
      entity.scope === 'team'
        ? await fetchTeamStripeAccount(entity.id)
        : await fetchOrganizationStripeAccount(entity.id);
    if (hasLinkableStripeAccount(account)) {
      return { ...entity, account: account! };
    }
  }
  return null;
}

export async function linkSharedStripeToEntities(
  source: ManagedStripeEntity,
  targets: ManagedStripeEntity[],
): Promise<number> {
  let linked = 0;
  for (const target of targets) {
    if (target.scope === source.scope && target.id === source.id) continue;
    try {
      await linkStripeConnectAccount(source.scope, source.id, target.scope, target.id);
      linked += 1;
    } catch (err) {
      console.error('linkSharedStripeToEntities', target, err);
    }
  }
  return linked;
}
